import React, { useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { HighlightParts, SuggestGroup, SuggestItem } from 'lib/suggest-groups'
import CommonnessChip from '../dictionary/commonness-chip'

type Props = {
   groups: SuggestGroup[]
   /**
    * The row ⏎ would open — moved onto with ↑↓ — or -1 when nothing is picked
    * and ⏎ should search the typed text instead.
    */
   selectedIndex: number
   /** The row under the pointer, or -1. Highlighted, but never the ⏎ target. */
   hoveredIndex: number
   /** Flattened items, in the same order the groups render them. */
   items: SuggestItem[]
   onHover: (index: number) => void
   /** A row was taken. The index is the row's position, for the analytics. */
   onPick: (index: number) => void
   /** What's in the box right now — what the footer's "⏎ to search" quotes. */
   rawQuery: string
   listboxId: string
   optionId: (index: number) => string
}

/** The matched run of a string, picked out of its surroundings. */
const Highlighted = ({ parts }: { parts: HighlightParts }) => {
   if (!parts.mid) return <>{parts.pre}</>
   return (
      <>
         {parts.pre}
         <b className="font-bold text-bodyBlack">{parts.mid}</b>
         {parts.post}
      </>
   )
}

const GROUP_LABEL_CLASS =
   'text-[12px] font-bold tracking-[1.2px] uppercase text-textMuted leading-none'

const SuggestRow = ({
   item,
   index,
   isSelected,
   isHovered,
   onHover,
   onPick,
   optionId,
}: {
   item: SuggestItem
   index: number
   isSelected: boolean
   isHovered: boolean
   onHover: (index: number) => void
   onPick: (index: number) => void
   optionId: string
}) => {
   const ref = useRef<HTMLAnchorElement | null>(null)

   // ↑↓ can walk past the eight-or-so rows the panel shows at once, and a
   // selection you can't see is worse than none — ⏎ would then open something
   // off-screen. 'nearest' scrolls only as far as it has to, so stepping down a
   // visible list doesn't jerk the panel around.
   useEffect(() => {
      if (isSelected) ref.current?.scrollIntoView({ block: 'nearest' })
   }, [isSelected])

   return (
      // A real anchor, as the search-result lists are: cmd/middle-click and "open
      // in new tab" have to keep working, which a div with an onClick would break.
      <Link
         ref={ref}
         {...item.route}
         id={optionId}
         role="option"
         aria-selected={isSelected}
         // Both states get the wash, but only the keyboard selection gets the
         // left bar — the bar is the promise that ⏎ opens this row, and a row
         // the pointer merely passed over hasn't made that promise.
         className={`flex items-center gap-[16px] px-[18px] pt-[9px] pb-[10px] no-underline text-inherit cursor-pointer ${
            isSelected
               ? 'bg-lightBlueTransparent shadow-[inset_3px_0_0_var(--color-lightBlue)]'
               : isHovered
                 ? 'bg-lightBlueTransparent'
                 : ''
         }`}
         onMouseEnter={() => onHover(index)}
         onClick={() => onPick(index)}
      >
         <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-[11px] flex-wrap">
               <span
                  className={`leading-[1.35] text-thaiWord ${
                     item.isThaiHeadword ? 'font-thai text-[22px]' : 'font-latin text-[19px]'
                  }`}
               >
                  <Highlighted parts={item.head} />
               </span>
               {item.translit.pre || item.translit.mid ? (
                  <span className="font-translit text-[16px] italic text-translit">
                     <Highlighted parts={item.translit} />
                  </span>
               ) : null}
            </div>
            <div
               className={`mt-[1px] text-[15px] text-bodyGray whitespace-nowrap overflow-hidden text-ellipsis ${
                  item.isThaiHeadword ? 'font-latin' : 'font-thai'
               }`}
            >
               <Highlighted parts={item.meaning} />
            </div>
         </div>
         <CommonnessChip commonnessScore={item.commonnessScore} display="meter" />
      </Link>
   )
}

/**
 * The drop panel under the dictionary search box: the entries the dictionary
 * has for what's currently typed, and nothing else. A phrase gets no panel (see
 * classifySuggestQuery), and neither does a lookup that matched nothing — a
 * panel with no rows in it has nothing to autocomplete, and ⏎ already goes to
 * /?q=, which is where "did you mean" and the translator live.
 *
 * Presentational only. Selection lives in the search bar, because the keyboard
 * that drives it belongs to the input.
 */
const SuggestPanel = ({
   groups,
   selectedIndex,
   hoveredIndex,
   items,
   onHover,
   onPick,
   rawQuery,
   listboxId,
   optionId,
}: Props) => {
   const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined
   let flatIndex = -1

   return (
      // No height cap and no scroller: an answer is at most five rows, or three
      // per language when a query reaches both, so the whole list always fits.
      // A scrollable panel would hide rows behind a gesture for no reason — and
      // while it scrolled, it clipped the commonness tooltip above each row.
      // The shadow carries the whole lift: nothing dims the page behind the
      // panel, so the drop alone has to separate it from the content it covers.
      <div className="absolute top-full left-0 right-0 mt-[5px] z-40 flex flex-col overflow-hidden rounded-[10px_10px_14px_14px] border border-hairline bg-white shadow-[0_20px_54px_rgba(20,30,50,0.24),0_6px_16px_rgba(20,30,50,0.12),0_1px_3px_rgba(20,30,50,0.08)]">
         <div id={listboxId} role="listbox">
            {groups.map((group) => (
               <div key={group.label} className="border-t border-transparent">
                  <div className="flex items-center px-[18px] pt-[13px] pb-[5px]">
                     <span className={GROUP_LABEL_CLASS}>{group.label}</span>
                  </div>
                  {group.items.map((item) => {
                     flatIndex += 1
                     const index = flatIndex
                     return (
                        <SuggestRow
                           key={`${group.label}-${item.word}`}
                           item={item}
                           index={index}
                           isSelected={index === selectedIndex}
                           isHovered={index === hoveredIndex}
                           onHover={onHover}
                           onPick={onPick}
                           optionId={optionId(index)}
                        />
                     )
                  })}
               </div>
            ))}
         </div>

         {items.length > 0 && (
            <div className="flex-none flex items-center justify-between gap-[16px] border-t border-hairline bg-suggestFooterBg px-[18px] py-[10px] text-[13px] text-textMuted">
               {/* Says what ⏎ does right now, which is the whole reason the
                   panel doesn't preselect a row: until one is picked, ⏎ searches
                   what was typed. */}
               {selected ? (
                  <span>
                     Press ⏎ to open{' '}
                     <b
                        className={`font-bold text-bodyBlack ${
                           selected.isThaiHeadword ? 'font-thai' : 'font-latin'
                        }`}
                     >
                        {selected.word}
                     </b>
                  </span>
               ) : (
                  <span>
                     Press ⏎ to search{' '}
                     <b className="font-bold text-bodyBlack">“{rawQuery.trim()}”</b>
                  </span>
               )}
               <span>↑↓ browse · esc close</span>
            </div>
         )}
      </div>
   )
}

export default SuggestPanel
